import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DoctorAppointment } from '../../../api/doctorappointment';

// INTERFACE_DoctorAppointment_REQUEST
interface DoctorAppointmentRequest {
  policy_number: string;
  full_name: string;
  birth_date: string;
  phone_number: string;
  email: string;
  complaint: string;
  appointment_date: string;
  hospital_chooser: "SELF" | string;
  doctor_name?: string;
  status: "PENDING" | string;
  user: number;
  hospital: number;
  platform: "WEB";
}

// INTERFACE_DoctorAppointment_RESPONSE
interface DoctorAppointmentResponse {
  id?: number;
  policy_number: string;
  full_name: string;
  birth_date: string;
  phone_number: string;
  email: string;
  complaint: string;
  appointment_date: string;
  hospital_chooser: string;
  doctor_name?: string;
  status: string;
  user: number;
  hospital: number;
  platform: string;
  message?: string;
  created_at?: string;
}

// INTERFACE_ERROR_DETAIL
interface ErrorDetail {
  code: string;
  detail: string;
  attr: string;
}

// INTERFACE_DoctorAppointment_STATE
interface DoctorAppointmentState {
  data: DoctorAppointmentResponse | null;
  loading: boolean;
  error: string | null;
  validationErrors: ErrorDetail[] | null;
  success: boolean;
}

// INITIAL_STATE
const initialState: DoctorAppointmentState = {
  data: null,
  loading: false,
  error: null,
  validationErrors: null,
  success: false,
};

// ASYNC_THUNK - POST DoctorAppointment
export const createDoctorAppointment = createAsyncThunk(
  'doctorAppointment/create',
  async (
    { requestData, language }: { requestData: DoctorAppointmentRequest; language?: 'az' | 'en' },
    { rejectWithValue }
  ) => {
    try {
      const result = await DoctorAppointment(requestData, language || 'az');

      if (result.status === 200 || result.status === 201) {
        return result.data as DoctorAppointmentResponse;
      } else {
        return rejectWithValue(result.data);
      }
    } catch (error) {
      return rejectWithValue({
        error: error instanceof Error ? error.message : 'Gözlənilməz xəta baş verdi'
      });
    }
  }
);

// DoctorAppointment_SLICE
const doctorAppointmentSlice = createSlice({
  name: 'doctorAppointment',
  initialState,
  reducers: {
    // Reset DoctorAppointment state
    resetDoctorAppointment: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
      state.validationErrors = null;
      state.success = false;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
      state.validationErrors = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Doctor Appointment - Pending
      .addCase(createDoctorAppointment.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.validationErrors = null;
        state.success = false;
      })
      // Create Doctor Appointment - Fulfilled
      .addCase(createDoctorAppointment.fulfilled, (state, action: PayloadAction<DoctorAppointmentResponse>) => {
        state.loading = false;
        state.data = action.payload;
        state.success = true;
        state.error = null;
        state.validationErrors = null;
      })
      // Create Doctor Appointment - Rejected
      .addCase(createDoctorAppointment.rejected, (state, action) => {
        state.loading = false;
        state.success = false;

        const payload = action.payload as any;

        // Handle validation errors
        if (payload?.type === 'validation_error' && payload?.errors) {
          state.validationErrors = payload.errors;
          state.error = 'Məlumatların yoxlanılması zamanı xəta baş verdi';
        }
        // Handle generic errors
        else if (payload?.error) {
          state.error = payload.error;
        }
        // Fallback error
        else {
          state.error = 'Qeydiyyat zamanı xəta baş verdi';
        }
      });
  },
});

// EXPORT_ACTIONS
export const { resetDoctorAppointment, clearError } = doctorAppointmentSlice.actions;

// EXPORT_REDUCER
export default doctorAppointmentSlice.reducer;